import React from "react";
import { motion } from "framer-motion";
import { ChevronDown } from "lucide-react";

// Staff Augmentation content
const sections = [
  {
    title: "Skilled Talent Pool",
    text: `We provide highly skilled professionals ready to integrate seamlessly with your team. Our experts bring industry knowledge, technical expertise, and adaptability to ensure your projects succeed.`,
    img: "../../staff1.jpg",
  },
  {
    title: "Flexible Staffing Solutions",
    text: `Whether you need short-term assistance or long-term collaboration, our flexible staffing model ensures you have the right resources exactly when you need them.`,
    img: "../../staff2.jpg",
    reverse: true,
  },
  {
    title: "Cost-Effective Scaling",
    text: `Expand your team without the overhead of full-time hires. Our solutions allow you to scale efficiently, optimize budgets, and maintain high-quality output.`,
    img: "../../staff3.jpg",
  },
  {
    title: "Seamless Integration",
    text: `Our staff integrates into your existing processes and workflows smoothly. We focus on collaboration, communication, and productivity to maximize results.`,
    img: "../../staff4.jpg",
    reverse: true,
  },
];

const ScrollSection = ({ children }) => (
  <motion.div
    initial={{ opacity: 0, y: 60 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.7, ease: "easeOut" }}
    viewport={{ once: false, amount: 0.3 }}
  >
    {children}
  </motion.div>
);

export default function StaffAugmentationPage() {
  const handleScroll = () => {
    document.getElementById("staff-sections").scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="font-poppins text-gray-900 overflow-x-hidden scroll-smooth relative">
      {/* Background */}
      <div
        className="fixed inset-0 -z-10 bg-cover bg-center bg-fixed"
        style={{ backgroundImage: `url('../../staffa.jpg')` }}
      >
        <div className="absolute inset-0 bg-black/60"></div>
      </div>

      {/* Hero Section */}
      <ScrollSection>
        <section className="relative h-[85vh] sm:h-screen flex flex-col items-center justify-center text-center px-4">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold uppercase tracking-wider mb-5 text-white drop-shadow-lg"
          >
            Staff Augmentation
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            className="text-base sm:text-lg md:text-xl max-w-xl sm:max-w-2xl text-gray-300 leading-relaxed px-2"
          >
            Why our Staff Augmentation services are the best choice for your business
          </motion.p>

          {/* Scroll Arrow */}
          <motion.div
            className="absolute bottom-10 sm:bottom-20 left-1/2 -translate-x-1/2 cursor-pointer animate-bounce text-red-600"
            onClick={handleScroll}
            whileHover={{ scale: 1.2 }}
          >
            <ChevronDown size={38} className="sm:w-10 sm:h-10" />
          </motion.div>
        </section>
      </ScrollSection>

      {/* Sections */}
      <div id="staff-sections" className="max-w-6xl mx-auto py-16 sm:py-24 px-4 sm:px-6 md:px-10 space-y-20 sm:space-y-24 relative z-10">
        {sections.map(({ title, text, img, reverse }, idx) => (
          <ScrollSection key={idx}>
            <div
              className={`flex flex-col md:flex-row items-center gap-10 sm:gap-12 md:gap-16 ${
                reverse ? "md:flex-row-reverse" : ""
              }`}
            >
              {/* Text */}
              <motion.div
                className="flex-1 bg-white/90 backdrop-blur-xl p-6 sm:p-10 md:p-12 rounded-2xl shadow-[0_10px_25px_rgba(209,17,17,0.2)] hover:shadow-[0_14px_35px_rgba(209,17,17,0.35)] transition-all duration-300"
                whileHover={{ y: -5 }}
              >
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 relative after:block after:w-16 sm:after:w-20 after:h-1 after:bg-red-600 after:rounded mt-2">
                  {title}
                </h2>
                <p className="text-sm sm:text-base md:text-lg leading-relaxed text-gray-700">{text}</p>
              </motion.div>

              {/* Image */}
              <motion.div
                className="flex-1 text-center"
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.4 }}
              >
                <img
                  src={img}
                  alt={title}
                  className="w-full max-w-xs sm:max-w-sm md:max-w-md rounded-2xl shadow-[0_12px_30px_rgba(209,17,17,0.2)]"
                />
              </motion.div>
            </div>
          </ScrollSection>
        ))}
      </div>

      <style>{`
        @keyframes bounce {
          0%,100% { transform: translateY(0); }
          50% { transform: translateY(10px); }
        }
        .animate-bounce {
          animation: bounce 1.8s infinite;
        }
      `}</style>
    </div>
  );
}